import interstellarApi from './interstellarApi';

export interface AgentsMetrics {
  busy: number;
  available: number;
  offline: number;
  assigned: number;
  total: number;
  ringing?: number;
}

export interface AbandonedCallsMetrics {
  count: number;
  percentage: number;
}

/** `GET /freeswitch/overview?account_id=` — métricas en tiempo (casi) real por cuenta */
export interface CallCenterOverview {
  calls_waiting: number;
  agents: AgentsMetrics;
  answered_calls_today: number;
  abandoned_calls_today: AbandonedCallsMetrics;
  average_wait_time_seconds: number;
  maximum_wait_time_seconds: number;
}

export interface CallWaiting {
  uuid: string;
  queue: string;
  position: number;
  caller_id: string;
  wait_time_seconds: number;
}

export interface CallsWaitingResponse {
  total: number;
  calls: CallWaiting[];
}

export interface AgentStatus {
  agent_id: string;
  agent_name: string;
  extension: string;
  current_state: string;
  state_duration_seconds: number;
  last_call_seconds_ago: number;
  calls_handled_today: number;
}

export interface AgentsSummary {
  available: number;
  busy: number;
  ringing: number;
  total: number;
}

export interface AgentsStatusResponse {
  summary: AgentsSummary;
  agents: AgentStatus[];
}

export interface CallLog {
  uuid?: string;
  id?: string;
  destination_number?: string;
  callee_id_number?: string;
  caller_id_number?: string;
  caller_id_name?: string;
  start_stamp?: string;
  created_at?: string;
  billsec?: number;
  duration?: number;
  agent_name?: string;
  user_name?: string;
  hangup_cause?: string;
  status?: string;
  resolution_note?: string;
  recording_url?: string;
}

export interface CallLogsResponse {
  call_logs: CallLog[];
  total_count: number;
}

export interface AbandonedCall {
  uuid?: string;
  id?: string;
  caller_id_number?: string;
  caller_id_name?: string;
  destination_number?: string;
  module_name?: string;
  start_stamp: string;
  wait_time_seconds?: number;
  duration?: number;
  hangup_cause?: string;
}

export interface AbandonedCallsResponse {
  abandoned_calls: AbandonedCall[];
  total_count: number;
}

export interface CallFilters {
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  extension?: string;
}

export const fetchCallCenterOverview = async (accountId: number): Promise<CallCenterOverview> => {
  const response = await interstellarApi.get<CallCenterOverview>('/freeswitch/overview', {
    params: { account_id: accountId },
  });
  return response.data;
};

export const fetchCallsWaiting = async (accountId: number): Promise<CallsWaitingResponse> => {
  const response = await interstellarApi.get<CallsWaitingResponse>('/freeswitch/calls-waiting', {
    params: { account_id: accountId },
  });
  return response.data;
};

export const fetchAgentsStatus = async (accountId: number): Promise<AgentsStatusResponse> => {
  const response = await interstellarApi.get<AgentsStatusResponse>('/freeswitch/agents-status', {
    params: { account_id: accountId },
  });
  return response.data;
};

export const fetchCallLogs = async (
  accountId: number,
  page = 1,
  pageSize = 20,
  direction?: 'inbound' | 'outbound',
  filters: CallFilters = {}
): Promise<CallLogsResponse> => {
  let url = `/incoming-call-config/call-log?account_id=${accountId}&page=${page}&page_size=${pageSize}`;
  if (direction) {
    url += `&direction=${direction}`;
  }
  if (filters.search) {
    url += `&q=${encodeURIComponent(filters.search)}`;
  }
  if (filters.dateFrom) {
    url += `&date_from=${encodeURIComponent(filters.dateFrom)}`;
  }
  if (filters.dateTo) {
    url += `&date_to=${encodeURIComponent(filters.dateTo)}`;
  }
  if (filters.extension) {
    url += `&extension=${encodeURIComponent(filters.extension)}`;
  }
  const response = await interstellarApi.get<CallLogsResponse>(url);
  return response.data;
};

export const fetchAbandonedCalls = async (
  accountId: number,
  page = 1,
  pageSize = 20,
  filters: CallFilters = {}
): Promise<AbandonedCallsResponse> => {
  let url = `/freeswitch/abandoned-calls?account_id=${accountId}&page=${page}&page_size=${pageSize}`;
  if (filters.search) {
    url += `&q=${encodeURIComponent(filters.search)}`;
  }
  if (filters.dateFrom) {
    url += `&date_from=${encodeURIComponent(filters.dateFrom)}`;
  }
  if (filters.dateTo) {
    url += `&date_to=${encodeURIComponent(filters.dateTo)}`;
  }
  if (filters.extension) {
    url += `&extension=${encodeURIComponent(filters.extension)}`;
  }
  const response = await interstellarApi.get<AbandonedCallsResponse>(url);
  return response.data;
};

export type AccountHealthLevel = 'critical' | 'warning' | 'healthy' | 'inactive';

export interface AccountHealth {
  level: AccountHealthLevel;
  overview: CallCenterOverview;
}

export function classifyHealth(overview: CallCenterOverview): AccountHealthLevel {
  const { agents, calls_waiting, abandoned_calls_today, average_wait_time_seconds } = overview;

  if (agents.total === 0) return 'inactive';

  const hasCriticalQueue = calls_waiting > 0 && agents.available === 0;
  const hasCriticalAbandonment = abandoned_calls_today.percentage > 30;
  if (hasCriticalQueue || hasCriticalAbandonment) return 'critical';

  const highWait = average_wait_time_seconds > 120;
  const moderateAbandonment = abandoned_calls_today.percentage > 15;
  const queueBuilding = calls_waiting > 3;
  if (highWait || moderateAbandonment || queueBuilding) return 'warning';

  return 'healthy';
}

export const fetchRecording = async (recordingId: string, signal?: AbortSignal): Promise<Blob> => {
  const response = await interstellarApi.get<Blob>(
    `/incoming-call-config/recording/${recordingId}`,
    {
      responseType: 'blob',
      signal,
    }
  );
  return response.data;
};
